import { useReducer, useEffect, useMemo, useRef } from 'react';
import scrollIntoView from 'scroll-into-view';

import {
    errorLogger,
    fetchJSON,
    isset,
    wait,
    getHeaderHeight,
    uri
} from 'utils';
import types from '../types';
import reducer from '../store/reducer';
import {
    getActiveAdvancedFilterIndex,
    getFiltersNextStateForSingleCategoryDocs,
    getInitialState,
    getRequestBodyFromAdvancedFilters,
    getRequestBodyFromFilters,
    getResetedFilters,
    getActiveFilters,
    getActiveFilterWithSubcategories,
    getAdvancedFiltersStateWithResetedSelect,
    shouldShowPagination,
    searchValueIsChanged,
    setAdvancedFiltersState,
    setAccordionFiltersState,
    setListingStateToURL,
    setFiltersCheckboxGroupState
} from '../utils';
import {
    initAction,
    setItemsAction,
    setFiltersAction,
    setAdvancedFiltersAction,
    setSearchValueAction,
    setIsLoadingAction,
    setIsLoadingMoreAction,
    setIsSearchLoaderVisibleAction,
    setCurrentPageAction,
    setPaginationAction,
    setSelectedSortingAction
} from '../store/actions';
import { LISTING_FIRST_PAGE } from '../constants';

let abortController = null;

/**
 * @typedef Output
 * @type {Object}
 * @property {types.Filter[]} activeFilters
 * @property {types.Filter[]} activeFiltersWithSubcategories
 * @property {types.AdvancedFilter[]} advancedFilters
 * @property {types.VideoCard[] | types.DocumentsListingGroup[] | types.NewsCard[] | LocationCard[] | TradeShowCard[]} items
 * @property {types.Filter[]} filters
 * @property {types.Pagination} pagination
 * @property {string} paginationURLBase
 * @property {string} searchValue
 * @property {Object} sorting
 * @property {number} nextItemsAmount
 * @property {boolean} showPagination
 * @property {boolean} isLoading
 * @property {boolean} isLoadingMore
 * @property {boolean} isSearchLoaderVisible
 * @property {MutableRefObject<HTMLDivElement>} scrollAnchorRef
 * @property {Function} handleAdvancedFiltersSelectChange
 * @property {Function} handleShowAllClick
 * @property {Function} handlePageChange
 * @property {Function} handleLoadMoreCLick
 * @property {Function} handleSubmit
 * @property {Function} handleSearchInputChange
 * @property {Function} handleCheckboxChange
 * @property {Function} handleResetSelect
 * @property {Function} handleSortingChange
 * @property {Function} handleClearFilters
 * @property {Function} handleRadioChange
 */

/**
 * @typedef  Params
 * @type {Object}
 * @property {string}
 * @property {string} culture
 * @property {types.Filter[]} filters
 * @property {types.AdvancedFilter[]} advancedFilters
 * @property {string} siteName
 * @property {Object} sorting
 * @property {types.Pagination} pagination
 */

/**
 * @param {Params} initialData
 * @returns {Output}
 */
const useListingAPI = initialData => {
    const { api, culture, siteName, sorting } = initialData;

    const [
        {
            items,
            filters,
            advancedFilters,
            pagination,
            isLoading,
            isLoadingMore,
            isSearchLoaderVisible,
            searchValue,
            selectedSorting
        },
        dispatch
    ] = useReducer(
        reducer,
        getInitialState({
            filters: initialData.filters || [],
            advancedFilters: initialData.advancedFilters || [],
            pagination: initialData.pagination || {},
            sorting: sorting || {},
            url: window.location.href
        })
    );

    const scrollAnchorRef = useRef(null);

    const activeFilters = useMemo(
        () => getActiveFilters([...filters, ...advancedFilters]),
        [filters, advancedFilters]
    );

    const activeFiltersWithSubcategories = useMemo(
        () => getActiveFilterWithSubcategories(filters),
        [filters]
    );

    const scrollToAnchor = callback => {
        const scrollTarget = scrollAnchorRef && scrollAnchorRef.current;

        if (scrollTarget) {
            const topOffset = getHeaderHeight();

            scrollIntoView(
                scrollTarget,
                {
                    align: {
                        top: 0,
                        topOffset
                    }
                },
                callback
            );
        }
    };

    const paginationURLBase = useMemo(() => window.location.href, [
        filters,
        pagination
    ]);

    const showPagination = useMemo(
        () => shouldShowPagination(items, pagination),
        [items, pagination]
    );

    const nextItemsAmount = useMemo(() => {
        const { rowsCount, currentPage, rowsOnPage } = pagination;
        const remaining = rowsCount - currentPage * rowsOnPage;
        const limit = remaining > rowsOnPage ? rowsOnPage : remaining;

        return limit;
    }, [pagination]);

    const fetchData = (values, signal) =>
        fetchJSON({
            url: api,
            method: 'POST',
            body: JSON.stringify(values),
            signal
        });

    const handleFetchFilteredPageDataSuccess = (
        response,
        isMerging,
        searchSubmit
    ) => {
        dispatch(
            setItemsAction({
                items: response.payload.data,
                isMerging
            })
        );
        dispatch(setPaginationAction(response.payload.pagination));

        if (searchSubmit) {
            dispatch(setIsSearchLoaderVisibleAction(false));
        }

        // hide loader
        if (isMerging) {
            dispatch(setIsLoadingMoreAction(false));
        } else {
            dispatch(setIsLoadingAction(false));
        }
    };

    const handleFetchFilteredPageDataError = (
        error,
        isMerging,
        searchSubmit
    ) => {
        errorLogger(
            (error && error.data && error.data.message) ||
                `handleFetchFilteredPageDataError: ${error}`
        );

        // if we intentionally cancelled the request, we keep showing the loader
        // animation until the next fetch succeeds
        if (
            error &&
            error.originalError &&
            error.originalError.name === 'AbortError'
        ) {
            return;
        }

        if (searchSubmit) {
            dispatch(setIsSearchLoaderVisibleAction(false));
        }

        // hide loader
        if (isMerging) {
            dispatch(setIsLoadingMoreAction(false));
        } else {
            dispatch(setIsLoadingAction(false));
        }
    };

    const fetchFilteredPageData = async ({
        isMerging = false,
        updateURL = true,
        searchSubmit = false,
        page,
        nextFilters = filters,
        nextAdvancedFilters = advancedFilters,
        nextSearchValue = searchValue,
        nextSelectedSorting = selectedSorting
    }) => {
        if (updateURL) {
            setListingStateToURL({
                page,
                filters: nextFilters,
                advancedFilters: nextAdvancedFilters,
                searchValue: nextSearchValue,
                selectedSorting: nextSelectedSorting
            });
        }

        if (searchSubmit) {
            dispatch(setIsSearchLoaderVisibleAction(true));
        }

        if (isMerging) {
            dispatch(setIsLoadingMoreAction(true));
        } else {
            dispatch(setIsLoadingAction(true));
        }

        // cancel previous request
        if (abortController) {
            abortController.abort();
        }

        // make request cancellable
        abortController = new AbortController();
        const { signal } = abortController;

        try {
            // show loading animation
            await wait();

            const response = await fetchData(
                {
                    ...getRequestBodyFromFilters(nextFilters),
                    advancedFilters: getRequestBodyFromAdvancedFilters(
                        nextAdvancedFilters
                    ),
                    pageSize: pagination.rowsOnPage,
                    page,
                    sorting: nextSelectedSorting,
                    searchValue: nextSearchValue,
                    culture,
                    siteName
                },
                signal
            );

            handleFetchFilteredPageDataSuccess(
                response,
                isMerging,
                searchSubmit
            );
        } catch (error) {
            handleFetchFilteredPageDataError(error, isMerging, searchSubmit);
        }
    };

    const handleShowAllClick = (filterId, filterOptionId) => {
        const nextFilters = getFiltersNextStateForSingleCategoryDocs(
            filters,
            filterId,
            filterOptionId
        );

        dispatch(setFiltersAction(nextFilters));

        scrollToAnchor(() => {
            fetchFilteredPageData({
                page: LISTING_FIRST_PAGE,
                nextFilters
            });
        });
    };

    const handlePageChange = nextPage => {
        if (nextPage === pagination.currentPage) {
            return;
        }

        // better UX -> move viewport to the top of the page, so user knows
        // new page will arrive
        scrollToAnchor(() => {
            dispatch(setCurrentPageAction(nextPage));

            fetchFilteredPageData({ page: nextPage });
        });
    };

    const handleLoadMoreCLick = nextPage => {
        fetchFilteredPageData({ page: nextPage, isMerging: true });
    };

    const handleSearchInputChange = value => {
        dispatch(setSearchValueAction(value));
    };

    const handleSubmit = event => {
        event.preventDefault();

        if (!searchValueIsChanged(searchValue, window.location.href)) {
            return;
        }

        dispatch(setCurrentPageAction(LISTING_FIRST_PAGE));

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE,
            searchSubmit: true
        });
    };

    const handleCheckboxChange = (checkboxId, filterId, controlledFilterId) => {
        const nextFilters = setFiltersCheckboxGroupState({
            filters,
            id: checkboxId,
            filterId,
            controlledFilterId
        });

        dispatch(setFiltersAction(nextFilters));

        dispatch(setCurrentPageAction(LISTING_FIRST_PAGE));

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE,
            nextFilters
        });
    };

    const handleRadioChange = filterId => {
        const nextFilters = setAccordionFiltersState(filters, filterId);
        const activeAdvancedFilterIndex = getActiveAdvancedFilterIndex(
            nextFilters,
            initialData.advancedFilters
        );
        const nextAdvancedFilters = isset(activeAdvancedFilterIndex)
            ? initialData.advancedFilters[activeAdvancedFilterIndex].items
            : [];

        dispatch(setFiltersAction(nextFilters));
        dispatch(setAdvancedFiltersAction(nextAdvancedFilters));

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE,
            nextFilters,
            nextAdvancedFilters
        });
    };

    const handleAdvancedFiltersSelectChange = (option, filterId) => {
        const { value } = option;
        const nextAdvancedFilters = setAdvancedFiltersState(
            advancedFilters,
            filterId,
            value
        );

        // check if filter changed
        if (
            JSON.stringify(advancedFilters) ===
            JSON.stringify(nextAdvancedFilters)
        ) {
            return;
        }

        dispatch(setAdvancedFiltersAction(nextAdvancedFilters));
        dispatch(setCurrentPageAction(LISTING_FIRST_PAGE));

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE,
            nextAdvancedFilters
        });
    };

    const handleSortingChange = nextSelectOption => {
        const nextSelectedOption = sorting.options.find(
            option => option.value === nextSelectOption.value
        );
        const nextSelectedSorting = nextSelectedOption
            ? nextSelectedOption.value
            : '';

        dispatch(setSelectedSortingAction(nextSelectedSorting));

        dispatch(setCurrentPageAction(LISTING_FIRST_PAGE));

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE,
            nextSelectedSorting
        });
    };

    const handleResetSelect = filterId => {
        const nextAdvancedFilters = getAdvancedFiltersStateWithResetedSelect(
            advancedFilters,
            filterId
        );

        dispatch(setAdvancedFiltersAction(nextAdvancedFilters));

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE,
            nextAdvancedFilters
        });
    };

    const handleClearFilters = () => {
        const query = uri.getParams(window.location.href);
        const paramsKeys = Object.keys(query);

        if (paramsKeys.length === 0) {
            return;
        }

        const nextFilters = getResetedFilters(filters);
        const nextAdvancedFilters = getResetedFilters(advancedFilters);

        dispatch(setFiltersAction(nextFilters));
        dispatch(setCurrentPageAction(LISTING_FIRST_PAGE));

        if (nextAdvancedFilters && nextAdvancedFilters.length) {
            dispatch(setAdvancedFiltersAction(nextAdvancedFilters));
        }

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE,
            nextFilters,
            nextAdvancedFilters
        });
    };

    const setStateByCurrentURL = () => {
        const nextState = getInitialState({
            advancedFilters: initialData.advancedFilters || [],
            items,
            pagination,
            filters,
            sorting,
            url: window.location.href
        });

        dispatch(initAction(nextState));

        fetchFilteredPageData({
            page: nextState.pagination.currentPage,
            nextFilters: nextState.filters,
            nextSearchValue: nextState.searchValue,
            nextSelectedSorting: nextState.selectedSorting,
            updateURL: false
        });
    };

    const handlePopStateChange = () => setStateByCurrentURL();

    useEffect(() => {
        // listen for going back/forward in history
        const onPopStateListener = () => handlePopStateChange();

        window.addEventListener('popstate', onPopStateListener);

        return () => {
            if (onPopStateListener) {
                window.removeEventListener('popstate', onPopStateListener);
            }
        };
    }, [handlePopStateChange]);

    useEffect(() => {
        fetchFilteredPageData({ page: pagination.currentPage });
    }, []);

    return {
        activeFilters,
        activeFiltersWithSubcategories,
        advancedFilters,
        items,
        filters,
        searchValue,
        selectedSorting,
        pagination,
        paginationURLBase,
        nextItemsAmount,
        showPagination,
        sorting,
        isLoading,
        isLoadingMore,
        isSearchLoaderVisible,
        scrollAnchorRef,
        handleAdvancedFiltersSelectChange,
        handleShowAllClick,
        handlePageChange,
        handleLoadMoreCLick,
        handleSubmit,
        handleSearchInputChange,
        handleCheckboxChange,
        handleResetSelect,
        handleSortingChange,
        handleClearFilters,
        handleRadioChange
    };
};

export default useListingAPI;
